import { Box, Button, Divider, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ArticleAccount } from 'src/shared/models/invoiceGroup/articleAccount';
import { getArticleAccountsByOwnerId, saveArticleAccount } from 'src/shared/services/articleAccountService';
import { getIntegrationsByOwnerId } from 'src/shared/services/accountingIntegrationService';
import { FortnoxAccountPicker } from './fortnoxAccountPicker';

const isValidAccount = (value: string) => value === '' || (/^\d+$/.test(value) && value.length <= 6);

export const AdminInvoiceGroupArticleAccountsTab = () => {
  const params = useParams();
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();
  const ownerId = params.id ? Number(params.id) : 0;

  const [articles, setArticles] = useState<ArticleAccount[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fortnoxProvider, setFortnoxProvider] = useState<string | null>(null);

  const fetchArticleAccounts = useCallback(async () => {
    if (ownerId <= 0) return;
    setLoading(true);
    try {
      const result = await getArticleAccountsByOwnerId(ownerId);
      if (!isMounted()) return;
      setArticles(result.data);
      setValues(
        result.data.reduce((acc: Record<string, string>, item) => {
          acc[item.articleNr] = item.account ?? '';
          return acc;
        }, {}),
      );
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [ownerId, isMounted]);

  useEffect(() => {
    fetchArticleAccounts();
  }, [fetchArticleAccounts]);

  useEffect(() => {
    if (ownerId <= 0) return;
    getIntegrationsByOwnerId(ownerId)
      .then((res) => {
        const active = res.data.find((i) => i.isActive === 1);
        setFortnoxProvider(active?.provider ?? null);
      })
      .catch(() => setFortnoxProvider(null));
  }, [ownerId]);

  const changed = useMemo(() => articles.filter((item) => (values[item.articleNr] ?? '') !== (item.account ?? '')), [articles, values]);

  const hasInvalid = useMemo(() => Object.values(values).some((value) => !isValidAccount(value)), [values]);

  const onSave = async () => {
    setSaving(true);
    try {
      // En artikel i taget. Listan är kort och ett fel på en rad ska inte dra med sig de andra.
      for (let i = 0; i < changed.length; i += 1) {
        const item = changed[i];
        // eslint-disable-next-line no-await-in-loop
        await saveArticleAccount({ owner_id: ownerId, articleNr: item.articleNr, account: values[item.articleNr] ?? '' });
      }
      if (isMounted()) {
        toast.success(<>{t('invoice:adminInvoiceGroupArticleAccountsTab.saveSuccess')}</>);
        fetchArticleAccounts();
      }
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(<>{t('invoice:adminInvoiceGroupArticleAccountsTab.saveError')}</>);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  const renderRows = (items: ArticleAccount[]) =>
    items.map((item) => {
      const value = values[item.articleNr] ?? '';
      const invalid = !isValidAccount(value);

      return (
        <TableRow key={item.articleNr}>
          <TableCell sx={{ width: 90, verticalAlign: 'top', pt: 3 }}>{item.articleNr}</TableCell>
          <TableCell sx={{ verticalAlign: 'top', pt: 3 }}>
            <Typography variant='body2'>{item.name}</Typography>
            {item.hasConflict && (
              <Typography variant='caption' color={(theme) => theme.palette.warning.main}>
                {t('invoice:adminInvoiceGroupArticleAccountsTab.conflictWarning', { name: item.conflictingName })}
              </Typography>
            )}
          </TableCell>
          <TableCell sx={{ width: 260 }}>
            <Stack direction='row' spacing={1} alignItems='flex-start'>
              <TextField
                size='small'
                fullWidth
                value={value}
                error={invalid}
                helperText={invalid ? t('invoice:adminInvoiceGroupArticleAccountsTab.validationAccount') : undefined}
                onChange={(event) => setValues((prev) => ({ ...prev, [item.articleNr]: event.target.value }))}
              />
              {fortnoxProvider && (
                <FortnoxAccountPicker
                  ownerId={ownerId}
                  provider={fortnoxProvider}
                  onSelect={(accountNumber) => setValues((prev) => ({ ...prev, [item.articleNr]: accountNumber }))}
                />
              )}
            </Stack>
          </TableCell>
        </TableRow>
      );
    });

  const renderTable = (items: ArticleAccount[], titleKey: string, descriptionKey: string) => (
    <Grid container spacing={2} mb={4}>
      <Grid item xs={12} lg={8}>
        <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
          {t(titleKey)}
        </Typography>
        <Typography variant='body2' color={(theme) => theme.palette.text.secondary} mb={1}>
          {t(descriptionKey)}
        </Typography>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>{t('invoice:adminInvoiceGroupArticleAccountsTab.articleNr')}</TableCell>
              <TableCell>{t('invoice:adminInvoiceGroupArticleAccountsTab.articleName')}</TableCell>
              <TableCell>{t('invoice:adminInvoiceGroupArticleAccountsTab.account')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows(items)}</TableBody>
        </Table>
      </Grid>
    </Grid>
  );

  const systemArticles = articles.filter((item) => item.isSystemArticle);
  const ownArticles = articles.filter((item) => !item.isSystemArticle);

  if (loading) return null;

  return (
    <Box>
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} lg={8}>
          <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupArticleAccountsTab.title')}
          </Typography>
          <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
            {t('invoice:adminInvoiceGroupArticleAccountsTab.description')}
          </Typography>
        </Grid>
      </Grid>

      {renderTable(
        systemArticles,
        'invoice:adminInvoiceGroupArticleAccountsTab.systemArticlesTitle',
        'invoice:adminInvoiceGroupArticleAccountsTab.systemArticlesDescription',
      )}

      {ownArticles.length > 0 &&
        renderTable(
          ownArticles,
          'invoice:adminInvoiceGroupArticleAccountsTab.ownArticlesTitle',
          'invoice:adminInvoiceGroupArticleAccountsTab.ownArticlesDescription',
        )}

      <Divider />
      <Grid display='flex' justifyContent='flex-end' mt={2}>
        <Button type='button' variant='contained' disableElevation disabled={changed.length === 0 || hasInvalid || saving} onClick={onSave}>
          {t('common:button.save')}
        </Button>
      </Grid>
    </Box>
  );
};
